import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import "./AdminDashboard.css";
import {
  FiUsers,
  FiUserCheck,
  FiShield,
  FiTrendingUp,
  FiSearch,
  FiTrash2,
  FiPower,
  FiEdit,
  FiPlus,
  FiSettings,
  FiLock,
  FiUnlock,
  FiCpu,
  FiActivity,
  FiCheck,
  FiRefreshCw,
  FiEye,
  FiStar,
  FiMenu,
  FiX,
} from "react-icons/fi";
import { FaChartBar, FaRobot } from "react-icons/fa";
import { servicesAPI, aiModelsAPI, usersAPI, rolesAPI } from "../../services/api";
import { useToast } from "../Toast/Toast";

const AdminDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL or default to "dashboard"
  const getActiveTabFromURL = useCallback(() => {
    const tabFromURL = searchParams.get('tab');
    const validTabs = ['dashboard', 'users', 'services', 'ai-models', 'ai-usage', 'roles'];
    if (tabFromURL && validTabs.includes(tabFromURL)) {
      return tabFromURL;
    }
    // Fallback to localStorage if no URL param
    const savedTab = localStorage.getItem('adminDashboardActiveTab');
    if (savedTab && validTabs.includes(savedTab)) {
      return savedTab;
    }
    return "dashboard";
  }, [searchParams]);

  const [activeTab, setActiveTab] = useState(getActiveTabFromURL);

  // Update URL when tab changes
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
    // Also save to localStorage as backup
    localStorage.setItem('adminDashboardActiveTab', tab);
  }, [setSearchParams]);

  // Sync with URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabFromURL = getActiveTabFromURL();
    if (tabFromURL !== activeTab) {
      setActiveTab(tabFromURL);
    }
  }, [searchParams, getActiveTabFromURL, activeTab]);

  // Initialize URL if not present
  useEffect(() => {
    if (!searchParams.get('tab')) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [usageLogs, setUsageLogs] = useState([]);
  const [providerOptions, setProviderOptions] = useState({ providers: [], models: {} });

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    adminUsers: 0,
    professionalUsers: 0,
    corporateUsers: 0,
    verifiedUsers: 0,
    totalServices: 0,
    activeServices: 0,
    totalAiModels: 0,
    activeAiModels: 0,
  });

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState("all");

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showAiModelModal, setShowAiModelModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showUsageDetailModal, setShowUsageDetailModal] = useState(false);

  const [editingService, setEditingService] = useState(null);
  const [editingAiModel, setEditingAiModel] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedUsageLog, setSelectedUsageLog] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [serviceForm, setServiceForm] = useState({
    name: "",
    slug: "",
    description: "",
    price: "",
    category: "document_processing",
    processingType: "automatic",
    estimatedTime: "",
    isActive: true,
    requiredDocuments: [],
    instructions: "",
  });

  const [serviceValidationErrors, setServiceValidationErrors] = useState({});
  const [aiModelValidationErrors, setAiModelValidationErrors] = useState({});
  const [roleValidationErrors, setRoleValidationErrors] = useState({});

  const [aiModelForm, setAiModelForm] = useState({
    name: "",
    provider: "openai",
    modelId: "gpt-4o",
    apiKey: "",
    baseUrl: "",
    isDefault: false,
    isActive: true,
    config: {
      temperature: 0.7,
      maxTokens: 4000,
      topP: 1,
    },
    description: "",
  });

  const [roleForm, setRoleForm] = useState({
    name: "",
    displayName: "",
    description: "",
    permissions: [],
    isActive: true,
  });

  const toast = useToast();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      if (response.success) {
        setUsers(response.data.users || []);
        setStats((prev) => ({
          ...prev,
          totalUsers: response.data.users?.length || 0,
          activeUsers: response.data.users?.filter((u) => u.isActive).length || 0,
          adminUsers: response.data.users?.filter((u) => u.roleName === "admin" || u.roleName === "super_admin").length || 0,
          professionalUsers: response.data.users?.filter((u) => u.roleName === "professional").length || 0,
          corporateUsers: response.data.users?.filter((u) => u.roleName === "corporate").length || 0,
          verifiedUsers: response.data.users?.filter((u) => u.isPhoneVerified).length || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchAiModels = useCallback(async () => {
    setLoading(true);
    try {
      const response = await aiModelsAPI.getAll();
      if (response.success) {
        setAiModels(response.data.models || []);
        setStats((prev) => ({
          ...prev,
          totalAiModels: response.data.models?.length || 0,
          activeAiModels: response.data.models?.filter((m) => m.isActive).length || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching AI models:", error);
      toast.error("Failed to fetch AI models");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rolesAPI.getAll();
      if (response.success) {
        setRoles(response.data.roles || []);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      toast.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch data on component mount
  useEffect(() => {
    fetchDashboardData();
    // Also fetch data for the initial active tab
    if (activeTab === "dashboard") {
      fetchUsers();
    } else if (activeTab === "ai-models") {
      fetchAiModels();
      fetchProviderOptions();
    } else if (activeTab === "ai-usage") {
      fetchUsageSummary();
      fetchUsageLogs();
    } else if (activeTab === "roles") {
      fetchRoles();
      fetchPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === "ai-models") {
      fetchAiModels();
      fetchProviderOptions();
    } else if (activeTab === "ai-usage") {
      fetchUsageSummary();
      fetchUsageLogs();
    } else if (activeTab === "roles") {
      fetchRoles();
      fetchPermissions();
    } else if (activeTab === "users") {
      fetchUsers();
    }
  }, [activeTab, fetchAiModels, fetchRoles, fetchUsers]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const servicesResponse = await servicesAPI.getAllServices();
      if (servicesResponse.success) {
        setServices(servicesResponse.data.services || []);
        setStats((prev) => ({
          ...prev,
          totalServices: servicesResponse.data.services?.length || 0,
          activeServices: servicesResponse.data.services?.filter((s) => s.isActive).length || 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderOptions = async () => {
    try {
      const response = await aiModelsAPI.getProviders();
      if (response.success) {
        setProviderOptions(response.data);
      }
    } catch (error) {
      console.error("Error fetching provider options:", error);
    }
  };

  const fetchUsageSummary = async () => {
    try {
      const response = await aiModelsAPI.getUsageSummary();
      if (response.success) {
        setUsageSummary(response.data);
      }
    } catch (error) {
      console.error("Error fetching usage summary:", error);
    }
  };

  const fetchUsageLogs = async () => {
    try {
      const response = await aiModelsAPI.getUsageLogs({ limit: 50 });
      if (response.success) {
        setUsageLogs(response.data.logs || []);
      }
    } catch (error) {
      console.error("Error fetching usage logs:", error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await rolesAPI.getPermissions();
      if (response.success) {
        setPermissions(response.data.permissions || []);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  // User Management
  const handleUserAction = async (userId, action) => {
    try {
      if (action === "toggle-status") {
        const response = await usersAPI.toggleStatus(userId);
        if (response.success) {
          setUsers(users.map((u) => u._id === userId ? { ...u, isActive: !u.isActive } : u));
          toast.success("User status updated");
        }
      } else if (action === "delete") {
        const response = await usersAPI.delete(userId);
        if (response.success) {
          setUsers(users.filter((u) => u._id !== userId));
          toast.success("User deleted");
        }
      } else if (action === "lock") {
        const response = await usersAPI.lock(userId);
        if (response.success) {
          setUsers(users.map((u) => u._id === userId ? { ...u, lockUntil: new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) } : u));
          toast.success("User account locked");
        }
      } else if (action === "unlock") {
        const response = await usersAPI.unlock(userId);
        if (response.success) {
          setUsers(users.map((u) => u._id === userId ? { ...u, lockUntil: null } : u));
          toast.success("User account unlocked");
        }
      }
    } catch (error) {
      toast.error("Action failed");
    }
  };

  // Service Management
  const handleServiceAction = async (serviceId, action) => {
    try {
      if (action === "delete") {
        const response = await servicesAPI.deleteService(serviceId);
        if (response.success) {
          setServices(services.filter((s) => s._id !== serviceId));
          toast.success("Service deleted");
        }
      } else if (action === "toggle-status") {
        const service = services.find((s) => s._id === serviceId);
        const response = await servicesAPI.updateService(serviceId, { ...service, isActive: !service.isActive });
        if (response.success) {
          setServices(services.map((s) => s._id === serviceId ? { ...s, isActive: !s.isActive } : s));
          toast.success("Service status updated");
        }
      }
    } catch (error) {
      toast.error("Action failed");
    }
  };

  const handleCreateService = async () => {
    if (!validateServiceForm()) {
      return;
    }

    try {
      const response = await servicesAPI.createService(serviceForm);
      if (response.success) {
        setServices([...services, response.data.service]);
        setShowServiceModal(false);
        resetServiceForm();
        toast.success("Service created");
      }
    } catch (error) {
      toast.error("Failed to create service");
    }
  };

  const handleUpdateService = async () => {
    if (!validateServiceForm()) {
      return;
    }

    try {
      const response = await servicesAPI.updateService(editingService._id, serviceForm);
      if (response.success) {
        setServices(services.map((s) => s._id === editingService._id ? response.data.service : s));
        setShowServiceModal(false);
        setEditingService(null);
        resetServiceForm();
        toast.success("Service updated");
      }
    } catch (error) {
      toast.error("Failed to update service");
    }
  };

  // AI Model Management
  const handleCreateAiModel = async () => {
    if (!validateAiModelForm()) {
      return;
    }

    try {
      const response = await aiModelsAPI.create(aiModelForm);
      if (response.success) {
        setAiModels([...aiModels, response.data.model]);
        setShowAiModelModal(false);
        resetAiModelForm();
        toast.success("AI Model created");
      }
    } catch (error) {
      toast.error(error.message || "Failed to create model");
    }
  };

  const handleUpdateAiModel = async () => {
    if (!validateAiModelForm()) {
      return;
    }

    try {
      const response = await aiModelsAPI.update(editingAiModel._id, aiModelForm);
      if (response.success) {
        setAiModels(aiModels.map((m) => m._id === editingAiModel._id ? response.data.model : m));
        setShowAiModelModal(false);
        setEditingAiModel(null);
        resetAiModelForm();
        toast.success("AI Model updated");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update model");
    }
  };

  const handleDeleteAiModel = async (modelId) => {
    if (!window.confirm("Delete this AI model?")) return;
    try {
      const response = await aiModelsAPI.delete(modelId);
      if (response.success) {
        setAiModels(aiModels.filter((m) => m._id !== modelId));
        toast.success("AI Model deleted");
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete model");
    }
  };

  const handleTestAiModel = async (modelId) => {
    toast.info("Testing model...");
    try {
      const response = await aiModelsAPI.test(modelId);
      if (response.success) {
        toast.success("Model test passed!");
      } else {
        toast.error(response.message || "Model test failed");
      }
    } catch (error) {
      toast.error(error.message || "Model test failed");
    }
  };

  const handleSetDefaultModel = async (modelId) => {
    try {
      const response = await aiModelsAPI.setDefault(modelId);
      if (response.success) {
        setAiModels(aiModels.map((m) => ({ ...m, isDefault: m._id === modelId })));
        toast.success("Default model updated");
      }
    } catch (error) {
      toast.error("Failed to set default model");
    }
  };

  // Role Management
  const handleCreateRole = async () => {
    if (!validateRoleForm()) {
      return;
    }

    try {
      const response = await rolesAPI.create(roleForm);
      if (response.success) {
        setRoles([...roles, response.data.role]);
        setShowRoleModal(false);
        resetRoleForm();
        toast.success("Role created");
      }
    } catch (error) {
      toast.error("Failed to create role");
    }
  };

  const handleUpdateRole = async () => {
    if (!validateRoleForm()) {
      return;
    }

    try {
      const response = await rolesAPI.update(editingRole._id, roleForm);
      if (response.success) {
        setRoles(roles.map((r) => r._id === editingRole._id ? response.data.role : r));
        setShowRoleModal(false);
        setEditingRole(null);
        resetRoleForm();
        toast.success("Role updated");
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm("Delete this role?")) return;
    try {
      const response = await rolesAPI.delete(roleId);
      if (response.success) {
        setRoles(roles.filter((r) => r._id !== roleId));
        toast.success("Role deleted");
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete role");
    }
  };

  // Form handlers
  const resetServiceForm = useCallback(() => {
    setServiceForm({
      name: "", slug: "", description: "", price: "",
      category: "document_processing", processingType: "automatic",
      estimatedTime: "", isActive: true, requiredDocuments: [], instructions: "",
    });
    setServiceValidationErrors({});
  }, []);

  // Validation function for service form
  const validateServiceForm = () => {
    const errors = {};

    if (!serviceForm.name || serviceForm.name.trim() === "") {
      errors.name = "Name required";
    }

    if (!serviceForm.description || serviceForm.description.trim() === "") {
      errors.description = "Description required";
    }

    if (!serviceForm.price || serviceForm.price === "" || isNaN(serviceForm.price) || parseFloat(serviceForm.price) <= 0) {
      errors.price = "Price required";
    }

    if (!serviceForm.estimatedTime || serviceForm.estimatedTime.trim() === "") {
      errors.estimatedTime = "Invalid value";
    }

    setServiceValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetAiModelForm = useCallback(() => {
    setAiModelForm({
      name: "", provider: "openai", modelId: "gpt-4o", apiKey: "", baseUrl: "",
      isDefault: false, isActive: true,
      config: { temperature: 0.7, maxTokens: 4000, topP: 1 },
      description: "",
    });
    setAiModelValidationErrors({});
  }, []);

  // Validation function for AI model form
  const validateAiModelForm = () => {
    const errors = {};

    if (!aiModelForm.name || aiModelForm.name.trim() === "") {
      errors.name = "Name required";
    }

    if (!aiModelForm.provider || aiModelForm.provider.trim() === "") {
      errors.provider = "Provider required";
    }

    if (!aiModelForm.modelId || aiModelForm.modelId.trim() === "") {
      errors.modelId = "Model ID required";
    }

    // API Key is required only when creating (not editing) or when editing and user wants to update it
    if (!editingAiModel && (!aiModelForm.apiKey || aiModelForm.apiKey.trim() === "")) {
      errors.apiKey = "API key required";
    }

    setAiModelValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetRoleForm = useCallback(() => {
    setRoleForm({
      name: "", displayName: "", description: "", permissions: [], isActive: true,
    });
    setRoleValidationErrors({});
  }, []);

  // Validation function for role form
  const validateRoleForm = () => {
    const errors = {};

    if (!roleForm.name || roleForm.name.trim() === "") {
      errors.name = "Name required";
    }

    if (!roleForm.displayName || roleForm.displayName.trim() === "") {
      errors.displayName = "Display name required";
    }

    setRoleValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditService = useCallback((service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name, slug: service.slug, description: service.description,
      price: service.price.toString(), category: service.category,
      processingType: service.processingType, estimatedTime: service.estimatedTime,
      isActive: service.isActive, requiredDocuments: service.requiredDocuments,
      instructions: service.instructions || "",
    });
    setServiceValidationErrors({});
    setShowServiceModal(true);
  }, []);

  const handleEditAiModel = useCallback((model) => {
    setEditingAiModel(model);
    setAiModelForm({
      name: model.name, provider: model.provider, modelId: model.modelId,
      apiKey: "", baseUrl: model.baseUrl || "", isDefault: model.isDefault,
      isActive: model.isActive, config: model.config || { temperature: 0.7, maxTokens: 4000, topP: 1 },
      description: model.description || "",
    });
    setAiModelValidationErrors({});
    setShowAiModelModal(true);
  }, []);

  const handleEditRole = useCallback((role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name, displayName: role.displayName || role.name,
      description: role.description || "",
      permissions: role.permissions?.map((p) => p._id || p) || [],
      isActive: role.isActive !== false,
    });
    setRoleValidationErrors({});
    setShowRoleModal(true);
  }, []);

  const generateSlug = useCallback((name) => {
    return name.toLowerCase().replace(/[^a-z0-9 -]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").trim("-");
  }, []);

  // Memoized filtered data
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch = user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.mobile?.includes(searchTerm);
      const matchesRole = roleFilter === "all" || user.roleName === roleFilter;
      const matchesStatus = statusFilter === "all" ||
        (statusFilter === "active" && user.isActive) ||
        (statusFilter === "inactive" && !user.isActive);
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  const filteredServices = useMemo(() => {
    return services.filter((service) => {
      const matchesSearch = service.name?.toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
        service.description?.toLowerCase().includes(serviceSearchTerm.toLowerCase());
      const matchesCategory = serviceCategoryFilter === "all" || service.category === serviceCategoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [services, serviceSearchTerm, serviceCategoryFilter]);

  // Auto-scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <div className="dashboard-stats">
            <div className="stats-grid">
              <div className="stat-card"><div className="stat-icon"><FiUsers /></div><div className="stat-content"><h3>{stats.totalUsers}</h3><p>Total Users</p></div></div>
              <div className="stat-card"><div className="stat-icon active"><FiUserCheck /></div><div className="stat-content"><h3>{stats.activeUsers}</h3><p>Active Users</p></div></div>
              <div className="stat-card"><div className="stat-icon professional"><FiStar /></div><div className="stat-content"><h3>{stats.professionalUsers}</h3><p>Professionals</p></div></div>
              <div className="stat-card"><div className="stat-icon corporate"><FiTrendingUp /></div><div className="stat-content"><h3>{stats.corporateUsers}</h3><p>Corporate</p></div></div>
              <div className="stat-card"><div className="stat-icon admin"><FiShield /></div><div className="stat-content"><h3>{stats.adminUsers}</h3><p>Admin Users</p></div></div>
              <div className="stat-card"><div className="stat-icon verified"><FiCheck /></div><div className="stat-content"><h3>{stats.verifiedUsers}</h3><p>Verified Users</p></div></div>
              <div className="stat-card"><div className="stat-icon"><FiSettings /></div><div className="stat-content"><h3>{stats.totalServices}</h3><p>Total Services</p></div></div>
              <div className="stat-card"><div className="stat-icon active"><FaRobot /></div><div className="stat-content"><h3>{stats.totalAiModels || 0}</h3><p>AI Models</p></div></div>
            </div>
          </div>
        );

      case "users":
        return (
          <div className="management-section">
            <div className="management-header">
              <h3>User Management</h3>
              <div className="management-controls">
                <div className="search-box">
                  <FiSearch />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                  <option value="all">All Roles</option>
                  <option value="user">Users</option>
                  <option value="professional">Professionals</option>
                  <option value="corporate">Corporate</option>
                  <option value="admin">Admins</option>
                  <option value="super_admin">Super Admins</option>
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <button className="btn-refresh" onClick={fetchUsers}><FiRefreshCw /></button>
              </div>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>User</th><th>Role</th><th>Status</th><th>Verified</th><th>Last Login</th><th>Actions</th>
                    <th>Lock/Unlock</th></tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="loading-cell">Loading...</td></tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr><td colSpan="7" className="empty-cell">No users found</td></tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user._id}>
                        <td>
                          <div className="user-info">
                            <div className="user-avatar">{user.fullName?.charAt(0).toUpperCase()}</div>
                            <div className="user-details">
                              <div className="user-name">{user.fullName}</div>
                              <div className="user-email">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td><span className={`badge role-${user.roleName}`}>{user.roleName?.replace("_", " ").toUpperCase()}</span></td>
                        <td><span className={`badge ${user.isActive ? "active" : "inactive"}`}>{user.isActive ? "Active" : "Inactive"}</span></td>
                        <td><span className={`badge ${user.isPhoneVerified ? "verified" : "unverified"}`}>{user.isPhoneVerified ? "Yes" : "No"}</span></td>
                        <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : "Never"}</td>
                        <td>
                          <div className="action-buttons">
                            <button className="action-btn" onClick={() => handleUserAction(user._id, "toggle-status")} title="Toggle Status"><FiPower /></button>
                            <button className="action-btn delete" onClick={() => handleUserAction(user._id, "delete")} title="Delete"><FiTrash2 /></button>
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className={`action-btn ${user.isLocked ? 'unlock' : 'lock'}`} 
                              onClick={() => handleUserAction(user._id, user.isLocked ? "unlock" : "lock")} 
                              title={user.isLocked ? "Unlock Account" : "Lock Account"}
                            >
                              {user.isLocked ? <FiUnlock /> : <FiLock />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case "services":
        return (
          <div className="management-section">
            <div className="management-header">
              <h3>Service Management</h3>
              <div className="management-controls">
                <button className="btn-primary" onClick={() => { setEditingService(null); resetServiceForm(); setServiceValidationErrors({}); setShowServiceModal(true); }}><FiPlus /> Add Service</button>
                <div className="search-box">
                  <FiSearch />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={serviceSearchTerm}
                    onChange={(e) => setServiceSearchTerm(e.target.value)}
                  />
                </div>
                <select value={serviceCategoryFilter} onChange={(e) => setServiceCategoryFilter(e.target.value)}>
                  <option value="all">All Categories</option>
                  <option value="document_processing">Document Processing</option>
                  <option value="legal_services">Legal Services</option>
                  <option value="compliance">Compliance</option>
                </select>
              </div>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Service</th><th>Category</th><th>Price</th><th>Type</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filteredServices.map((service) => (
                    <tr key={service._id}>
                      <td>
                        <div className="service-info">
                          <div className="service-name">{service.name}</div>
                          <div className="service-desc">{service.description?.substring(0, 50)}...</div>
                        </div>
                      </td>
                      <td><span className="badge category">{service.category?.replace("_", " ")}</span></td>
                      <td className="price">₹{service.price}</td>
                      <td><span className={`badge type-${service.processingType}`}>{service.processingType}</span></td>
                      <td><span className={`badge ${service.isActive ? "active" : "inactive"}`}>{service.isActive ? "Active" : "Inactive"}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn" onClick={() => handleEditService(service)}><FiEdit /></button>
                          <button className="action-btn" onClick={() => handleServiceAction(service._id, "toggle-status")}><FiPower /></button>
                          <button className="action-btn delete" onClick={() => handleServiceAction(service._id, "delete")}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case "ai-models":
        return (
          <div className="management-section">
            <div className="management-header">
              <h3>AI Model Configuration</h3>
              <div className="management-controls">
                <button className="btn-primary" onClick={() => { setEditingAiModel(null); resetAiModelForm(); setAiModelValidationErrors({}); setShowAiModelModal(true); }}><FiPlus /> Add Model</button>
                <button className="btn-refresh" onClick={fetchAiModels}><FiRefreshCw /></button>
              </div>
            </div>

            {/* Default Model Selection for Order Processing */}
            {aiModels.length > 0 && (
              <div className="default-model-selector">
                <div className="selector-header">
                  <FiCpu className="selector-icon" />
                  <div className="selector-info">
                    <h4>Default Model for Order Processing</h4>
                    <p>Select which AI model will be used by default for all document processing and analysis</p>
                  </div>
                </div>
                <div className="selector-control">
                  <select
                    value={aiModels.find(m => m.isDefault)?._id || ""}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleSetDefaultModel(e.target.value);
                      }
                    }}
                    className="default-model-select"
                  >
                    <option value="" disabled>Select default model...</option>
                    {aiModels.filter(m => m.isActive).map((model) => (
                      <option key={model._id} value={model._id}>
                        {model.provider === "openai" ? "🤖" : model.provider === "gemini" ? "✨" : model.provider === "anthropic" ? "🧠" : "☁️"} {model.name} ({model.provider}/{model.modelId})
                      </option>
                    ))}
                  </select>
                  {aiModels.find(m => m.isDefault) && (
                    <div className="current-default">
                      <FiCheck className="check-icon" />
                      <span>Currently using: <strong>{aiModels.find(m => m.isDefault)?.name}</strong></span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {aiModels.length === 0 ? (
              <div className="empty-state">
                <FaRobot className="empty-icon" />
                <h4>No AI Models Configured</h4>
                <p>Add an AI model to start processing documents with AI</p>
                <button className="btn-primary" onClick={() => { setEditingAiModel(null); resetAiModelForm(); setAiModelValidationErrors({}); setShowAiModelModal(true); }}><FiPlus /> Add Your First Model</button>
              </div>
            ) : (
              <div className="cards-grid">
                {aiModels.map((model) => (
                  <div key={model._id} className={`model-card ${model.isDefault ? "default" : ""} ${!model.isActive ? "inactive" : ""}`}>
                    {model.isDefault && <div className="default-badge"><FiStar /> Default</div>}
                    <div className="model-header">
                      <div className="model-icon">{model.provider === "openai" ? "🤖" : model.provider === "gemini" ? "✨" : model.provider === "anthropic" ? "🧠" : "☁️"}</div>
                      <div className="model-info">
                        <h4>{model.name}</h4>
                        <span className="model-id">{model.provider} / {model.modelId}</span>
                      </div>
                    </div>
                    <div className="model-stats">
                      <div className="stat"><span className="label">Requests</span><span className="value">{model.usageStats?.totalRequests || 0}</span></div>
                      <div className="stat"><span className="label">Tokens</span><span className="value">{(model.usageStats?.totalTokens || 0).toLocaleString()}</span></div>
                      <div className="stat"><span className="label">Errors</span><span className="value">{model.usageStats?.errors || 0}</span></div>
                    </div>
                    <div className="model-config">
                      <span>Temp: {model.config?.temperature}</span>
                      <span>Max Tokens: {model.config?.maxTokens}</span>
                    </div>
                    <div className="model-actions">
                      <button className="btn-sm" onClick={() => handleTestAiModel(model._id)} title="Test"><FiActivity /> Test</button>
                      <button className="btn-sm" onClick={() => handleEditAiModel(model)} title="Edit"><FiEdit /></button>
                      {!model.isDefault && <button className="btn-sm primary" onClick={() => handleSetDefaultModel(model._id)} title="Set Default"><FiStar /></button>}
                      <button className="btn-sm delete" onClick={() => handleDeleteAiModel(model._id)} title="Delete"><FiTrash2 /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "ai-usage":
        return (
          <div className="management-section">
            <div className="management-header">
              <h3>AI Usage Analytics</h3>
              <button className="btn-refresh" onClick={() => { fetchUsageSummary(); fetchUsageLogs(); }}><FiRefreshCw /></button>
            </div>

            {usageSummary && (
              <div className="usage-summary">
                <div className="summary-card"><h4>Total API Calls</h4><span className="value">{usageSummary.summary?.totalCalls || 0}</span></div>
                <div className="summary-card success"><h4>Successful</h4><span className="value">{usageSummary.summary?.successfulCalls || 0}</span></div>
                <div className="summary-card error"><h4>Failed</h4><span className="value">{usageSummary.summary?.failedCalls || 0}</span></div>
                <div className="summary-card"><h4>Total Tokens</h4><span className="value">{(usageSummary.summary?.totalTokens || 0).toLocaleString()}</span></div>
                <div className="summary-card"><h4>Est. Cost</h4><span className="value">${(usageSummary.summary?.totalCost || 0).toFixed(4)}</span></div>
                <div className="summary-card"><h4>Avg Latency</h4><span className="value">{Math.round(usageSummary.summary?.avgLatency || 0)}ms</span></div>
              </div>
            )}

            <h4 style={{ marginTop: "24px" }}>Recent API Calls</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr><th>Time</th><th>Model</th><th>Order</th><th>Tokens</th><th>Latency</th><th>Cost</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {usageLogs.map((log) => (
                    <tr key={log._id}>
                      <td>{new Date(log.createdAt).toLocaleString()}</td>
                      <td><span className="badge">{log.modelDetails?.provider}/{log.modelDetails?.modelId}</span></td>
                      <td>{log.orderNumber || "-"}</td>
                      <td>{log.tokens?.total || 0}</td>
                      <td>{log.performance?.latencyMs}ms</td>
                      <td>${(log.cost?.totalCost || 0).toFixed(6)}</td>
                      <td><span className={`badge ${log.status}`}>{log.status}</span></td>
                      <td><button className="action-btn" onClick={() => { setSelectedUsageLog(log); setShowUsageDetailModal(true); }}><FiEye /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case "roles":
        return (
          <div className="management-section">
            <div className="management-header">
              <h3>Roles & Permissions</h3>
              <div className="management-controls">
                <button className="btn-primary" onClick={() => { setEditingRole(null); resetRoleForm(); setRoleValidationErrors({}); setShowRoleModal(true); }}><FiPlus /> Add Role</button>
                <button className="btn-refresh" onClick={fetchRoles}><FiRefreshCw /></button>
              </div>
            </div>

            <div className="cards-grid">
              {roles.map((role) => (
                <div key={role._id} className="role-card">
                  <div className="role-header">
                    <h4>{role.displayName || role.name}</h4>
                    <span className="role-name">@{role.name}</span>
                  </div>
                  <p className="role-desc">{role.description || "No description"}</p>
                  <div className="permissions-list">
                    <strong>Permissions ({role.permissions?.length || 0}):</strong>
                    <div className="permissions-tags">
                      {role.permissions?.slice(0, 5).map((p, i) => (
                        <span key={i} className="permission-tag">{p.name || p}</span>
                      ))}
                      {role.permissions?.length > 5 && <span className="permission-tag more">+{role.permissions.length - 5} more</span>}
                    </div>
                  </div>
                  <div className="role-actions">
                    <button className="btn-sm" onClick={() => handleEditRole(role)}><FiEdit /> Edit</button>
                    {!["super_admin", "admin", "user"].includes(role.name) && (
                      <button className="btn-sm delete" onClick={() => handleDeleteRole(role._id)}><FiTrash2 /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <button className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <FiX /> : <FiMenu />}
        </button>
        <h2>Admin Dashboard</h2>
      </div>

      <div className={`admin-layout ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}
        <div className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <button
            className={`sidebar-item ${activeTab === "dashboard" ? "active" : ""}`}
            onClick={() => {
              handleTabChange("dashboard");
              setSidebarOpen(false);
            }}
          >
            <FaChartBar className="sidebar-icon" />
            <span>Dashboard</span>
          </button>
          <button
            className={`sidebar-item ${activeTab === "users" ? "active" : ""}`}
            onClick={() => {
              handleTabChange("users");
              setSidebarOpen(false);
            }}
          >
            <FiUsers className="sidebar-icon" />
            <span>Users</span>
          </button>
          <button
            className={`sidebar-item ${activeTab === "services" ? "active" : ""}`}
            onClick={() => {
              handleTabChange("services");
              setSidebarOpen(false);
            }}
          >
            <FiSettings className="sidebar-icon" />
            <span>Services</span>
          </button>
          <button
            className={`sidebar-item ${activeTab === "ai-models" ? "active" : ""}`}
            onClick={() => {
              handleTabChange("ai-models");
              setSidebarOpen(false);
            }}
          >
            <FaRobot className="sidebar-icon" />
            <span>AI Models</span>
          </button>
          <button
            className={`sidebar-item ${activeTab === "ai-usage" ? "active" : ""}`}
            onClick={() => {
              handleTabChange("ai-usage");
              setSidebarOpen(false);
            }}
          >
            <FiActivity className="sidebar-icon" />
            <span>AI Usage</span>
          </button>
          <button
            className={`sidebar-item ${activeTab === "roles" ? "active" : ""}`}
            onClick={() => {
              handleTabChange("roles");
              setSidebarOpen(false);
            }}
          >
            <FiShield className="sidebar-icon" />
            <span>Roles</span>
          </button>
        </div>

        <div className="admin-content">
          <div className="content-wrapper">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* AI Model Modal - Rendered inline to prevent focus loss */}
      {showAiModelModal && (
        <div className="modal-overlay" onClick={() => { setShowAiModelModal(false); setAiModelValidationErrors({}); }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingAiModel ? "Edit AI Model" : "Add AI Model"}</h3>
              <button className="modal-close" onClick={() => { setShowAiModelModal(false); setAiModelValidationErrors({}); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Model Name *</label>
                  <input
                    type="text"
                    value={aiModelForm.name}
                    onChange={(e) => {
                      setAiModelForm(prev => ({ ...prev, name: e.target.value }));
                      if (aiModelValidationErrors.name) {
                        setAiModelValidationErrors(prev => ({ ...prev, name: "" }));
                      }
                    }}
                    placeholder="My GPT-4 Model"
                    className={aiModelValidationErrors.name ? "error" : ""}
                  />
                  {aiModelValidationErrors.name && (
                    <div className="field-error">{aiModelValidationErrors.name}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Provider *</label>
                  <select
                    value={aiModelForm.provider}
                    onChange={(e) => {
                      setAiModelForm(prev => ({
                        ...prev,
                        provider: e.target.value,
                        modelId: providerOptions.models?.[e.target.value]?.[0]?.id || ""
                      }));
                      if (aiModelValidationErrors.provider) {
                        setAiModelValidationErrors(prev => ({ ...prev, provider: "" }));
                      }
                    }}
                    className={aiModelValidationErrors.provider ? "error" : ""}
                  >
                    {providerOptions.providers?.map((p) => (
                      <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
                    ))}
                  </select>
                  {aiModelValidationErrors.provider && (
                    <div className="field-error">{aiModelValidationErrors.provider}</div>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Model ID *</label>
                  <select
                    value={aiModelForm.modelId}
                    onChange={(e) => {
                      setAiModelForm(prev => ({ ...prev, modelId: e.target.value }));
                      if (aiModelValidationErrors.modelId) {
                        setAiModelValidationErrors(prev => ({ ...prev, modelId: "" }));
                      }
                    }}
                    className={aiModelValidationErrors.modelId ? "error" : ""}
                  >
                    {providerOptions.models?.[aiModelForm.provider]?.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                  {aiModelValidationErrors.modelId && (
                    <div className="field-error">{aiModelValidationErrors.modelId}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>API Key *</label>
                  <input
                    type="password"
                    value={aiModelForm.apiKey}
                    onChange={(e) => {
                      setAiModelForm(prev => ({ ...prev, apiKey: e.target.value }));
                      if (aiModelValidationErrors.apiKey) {
                        setAiModelValidationErrors(prev => ({ ...prev, apiKey: "" }));
                      }
                    }}
                    placeholder={editingAiModel ? "Leave empty to keep existing" : "sk-..."}
                    className={aiModelValidationErrors.apiKey ? "error" : ""}
                  />
                  {aiModelValidationErrors.apiKey && (
                    <div className="field-error">{aiModelValidationErrors.apiKey}</div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Base URL (optional)</label>
                <input
                  type="text"
                  value={aiModelForm.baseUrl}
                  onChange={(e) => setAiModelForm(prev => ({ ...prev, baseUrl: e.target.value }))}
                  placeholder="For Azure or custom endpoints"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Temperature ({aiModelForm.config.temperature})</label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={aiModelForm.config.temperature}
                    onChange={(e) => setAiModelForm(prev => ({
                      ...prev,
                      config: { ...prev.config, temperature: parseFloat(e.target.value) }
                    }))}
                  />
                </div>
                <div className="form-group">
                  <label>Max Tokens</label>
                  <input
                    type="number"
                    value={aiModelForm.config.maxTokens}
                    onChange={(e) => setAiModelForm(prev => ({
                      ...prev,
                      config: { ...prev.config, maxTokens: parseInt(e.target.value) || 4000 }
                    }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={aiModelForm.description}
                  onChange={(e) => setAiModelForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                  rows="2"
                />
              </div>
              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={aiModelForm.isActive}
                    onChange={(e) => setAiModelForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  <span className="checkbox-text-active">Active</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={aiModelForm.isDefault}
                    onChange={(e) => setAiModelForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                  />
                  <span className="checkbox-text-default">Set as Default</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAiModelModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={editingAiModel ? handleUpdateAiModel : handleCreateAiModel}>
                {editingAiModel ? "Update" : "Create"} Model
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Modal */}
      {showRoleModal && (
        <div className="modal-overlay" onClick={() => { setShowRoleModal(false); setRoleValidationErrors({}); }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRole ? "Edit Role" : "Create Role"}</h3>
              <button className="modal-close" onClick={() => { setShowRoleModal(false); setRoleValidationErrors({}); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Role Name (code) *</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => {
                      setRoleForm(prev => ({
                        ...prev,
                        name: e.target.value.toLowerCase().replace(/\s+/g, "_")
                      }));
                      if (roleValidationErrors.name) {
                        setRoleValidationErrors(prev => ({ ...prev, name: "" }));
                      }
                    }}
                    placeholder="custom_role"
                    disabled={!!editingRole}
                    className={roleValidationErrors.name ? "error" : ""}
                  />
                  {roleValidationErrors.name && (
                    <div className="field-error">{roleValidationErrors.name}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    value={roleForm.displayName}
                    onChange={(e) => {
                      setRoleForm(prev => ({ ...prev, displayName: e.target.value }));
                      if (roleValidationErrors.displayName) {
                        setRoleValidationErrors(prev => ({ ...prev, displayName: "" }));
                      }
                    }}
                    placeholder="Custom Role"
                    className={roleValidationErrors.displayName ? "error" : ""}
                  />
                  {roleValidationErrors.displayName && (
                    <div className="field-error">{roleValidationErrors.displayName}</div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={roleForm.description}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Role description"
                  rows="2"
                />
              </div>
              <div className="form-group">
                <label>Permissions</label>
                <div className="permissions-grid">
                  {permissions.map((perm) => (
                    <label key={perm._id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={roleForm.permissions.includes(perm._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setRoleForm(prev => ({
                              ...prev,
                              permissions: [...prev.permissions, perm._id]
                            }));
                          } else {
                            setRoleForm(prev => ({
                              ...prev,
                              permissions: prev.permissions.filter((p) => p !== perm._id)
                            }));
                          }
                        }}
                      />
                      {perm.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowRoleModal(false); setRoleValidationErrors({}); }}>Cancel</button>
              <button className="btn-primary" onClick={editingRole ? handleUpdateRole : handleCreateRole}>
                {editingRole ? "Update" : "Create"} Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Service Modal */}
      {showServiceModal && (
        <div className="modal-overlay" onClick={() => { setShowServiceModal(false); setServiceValidationErrors({}); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingService ? "Edit Service" : "Create Service"}</h3>
              <button className="modal-close" onClick={() => { setShowServiceModal(false); setServiceValidationErrors({}); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={serviceForm.name}
                  onChange={(e) => {
                    setServiceForm(prev => ({
                      ...prev,
                      name: e.target.value,
                      slug: generateSlug(e.target.value)
                    }));
                    if (serviceValidationErrors.name) {
                      setServiceValidationErrors(prev => ({ ...prev, name: "" }));
                    }
                  }}
                  className={serviceValidationErrors.name ? "error" : ""}
                />
                {serviceValidationErrors.name && (
                  <div className="field-error">{serviceValidationErrors.name}</div>
                )}
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input
                  type="text"
                  value={serviceForm.slug}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, slug: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={serviceForm.description}
                  onChange={(e) => {
                    setServiceForm(prev => ({ ...prev, description: e.target.value }));
                    if (serviceValidationErrors.description) {
                      setServiceValidationErrors(prev => ({ ...prev, description: "" }));
                    }
                  }}
                  rows="3"
                  className={serviceValidationErrors.description ? "error" : ""}
                />
                {serviceValidationErrors.description && (
                  <div className="field-error">{serviceValidationErrors.description}</div>
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Price (₹)</label>
                  <input
                    type="number"
                    value={serviceForm.price}
                    onChange={(e) => {
                      setServiceForm(prev => ({ ...prev, price: e.target.value }));
                      if (serviceValidationErrors.price) {
                        setServiceValidationErrors(prev => ({ ...prev, price: "" }));
                      }
                    }}
                    className={serviceValidationErrors.price ? "error" : ""}
                  />
                  {serviceValidationErrors.price && (
                    <div className="field-error">{serviceValidationErrors.price}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={serviceForm.category}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="document_processing">Document Processing</option>
                    <option value="legal_services">Legal Services</option>
                    <option value="compliance">Compliance</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={serviceForm.processingType}
                    onChange={(e) => setServiceForm(prev => ({ ...prev, processingType: e.target.value }))}
                  >
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Est. Time</label>
                  <input
                    type="text"
                    value={serviceForm.estimatedTime}
                    onChange={(e) => {
                      setServiceForm(prev => ({ ...prev, estimatedTime: e.target.value }));
                      if (serviceValidationErrors.estimatedTime) {
                        setServiceValidationErrors(prev => ({ ...prev, estimatedTime: "" }));
                      }
                    }}
                    placeholder="24 hours"
                    className={serviceValidationErrors.estimatedTime ? "error" : ""}
                  />
                  {serviceValidationErrors.estimatedTime && (
                    <div className="field-error">{serviceValidationErrors.estimatedTime}</div>
                  )}
                </div>
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={serviceForm.isActive}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                <span className="checkbox-text-active">Active</span>
              </label>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowServiceModal(false); setServiceValidationErrors({}); }}>Cancel</button>
              <button className="btn-primary" onClick={editingService ? handleUpdateService : handleCreateService}>
                {editingService ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Usage Detail Modal */}
      {showUsageDetailModal && selectedUsageLog && (
        <div className="modal-overlay" onClick={() => setShowUsageDetailModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>API Call Details</h3>
              <button className="modal-close" onClick={() => setShowUsageDetailModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="usage-detail">
                <div className="detail-grid">
                  <div className="detail-item"><label>Time</label><span>{new Date(selectedUsageLog.createdAt).toLocaleString()}</span></div>
                  <div className="detail-item"><label>Status</label><span className={`badge ${selectedUsageLog.status}`}>{selectedUsageLog.status}</span></div>
                  <div className="detail-item"><label>Model</label><span>{selectedUsageLog.modelDetails?.name} ({selectedUsageLog.modelDetails?.provider}/{selectedUsageLog.modelDetails?.modelId})</span></div>
                  <div className="detail-item"><label>Order</label><span>{selectedUsageLog.orderNumber || "N/A"}</span></div>
                  <div className="detail-item"><label>Latency</label><span>{selectedUsageLog.performance?.latencyMs}ms</span></div>
                  <div className="detail-item"><label>Service Type</label><span>{selectedUsageLog.serviceType}</span></div>
                </div>
                <div className="detail-section">
                  <h4>Token Usage</h4>
                  <div className="token-stats">
                    <div><label>Prompt</label><span>{selectedUsageLog.tokens?.prompt || 0}</span></div>
                    <div><label>Completion</label><span>{selectedUsageLog.tokens?.completion || 0}</span></div>
                    <div><label>Total</label><span>{selectedUsageLog.tokens?.total || 0}</span></div>
                  </div>
                </div>
                <div className="detail-section">
                  <h4>Cost</h4>
                  <div className="cost-stats">
                    <div><label>Input</label><span>${(selectedUsageLog.cost?.inputCost || 0).toFixed(6)}</span></div>
                    <div><label>Output</label><span>${(selectedUsageLog.cost?.outputCost || 0).toFixed(6)}</span></div>
                    <div><label>Total</label><span>${(selectedUsageLog.cost?.totalCost || 0).toFixed(6)}</span></div>
                  </div>
                </div>
                {selectedUsageLog.error && (
                  <div className="detail-section error">
                    <h4>Error</h4>
                    <p><strong>{selectedUsageLog.error.code}:</strong> {selectedUsageLog.error.message}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
